/*
 * Copyright (c) Nexign, JSC, 2022-2023.
 */
/**
 *
 * @author Ilya Babanov
 * @version 1.23.0
 * @since 1.22.0
 */

import type { IFormAddressSearchResponse } from '@/main/modules/formAddress/types/formAddressTypes';
import { subscriberIdApi } from '@/main/pages/PageSubscriberId/store';
import type { ISubscriberIdStatus } from '@/main/pages/PageSubscriberId/types';
import { formatSubscriberIdApiUrl } from '@/main/pages/PageSubscriberId/utils/formatApiUrl';
import type { TConflicts } from '@/main/types/globalTypes';
import type { IInputSearchItem } from '@components/common/InputSearch/types';
import { formAddressSearchMapping } from '@modules/formAddress/formAddressMapping';

import { FileId } from './const/SubscriberIdPersonalDataEdit';
import type { IPersonalDataFileResponse } from './mapping/file';
import { personalDataFileMapping } from './mapping/file';
import type { IPersonalDataInfoResponse } from './mapping/info';
import { personalDataInfoMapping } from './mapping/info';
import { personalDataSaveEditRequestMapping } from './mapping/info-edit';
import type { IPersonalDataMetaResponse } from './mapping/meta';
import { personalDataMetaMapping } from './mapping/meta';
import type {
  IPersonalDataFile,
  IPersonalDataFileUploadQuery,
  IPersonalDataNewFile,
  TNewFileId,
} from './types/file';
import type { IPersonalDataInfo, IPersonalDataInfoQuery } from './types/info';
import type {
  IPersonalDataInfoEditQuery,
  IPersonalDataSave,
} from './types/info-edit';
import type { IPersonalDataMeta } from './types/meta';

const personalDataWithTags = subscriberIdApi.enhanceEndpoints({
  addTagTypes: ['PersonalDataMeta', 'PersonalDataInfo'],
});

export const personalDataApi = personalDataWithTags.injectEndpoints({
  endpoints: (builder) => ({
    getPersonalDataMeta: builder.query<IPersonalDataMeta, string>({
      query: (subscriberId) =>
        formatSubscriberIdApiUrl(subscriberId, 'personal-data/meta'),
      transformResponse: (response: IPersonalDataMetaResponse) =>
        personalDataMetaMapping(response),
      providesTags: ['PersonalDataMeta'],
    }),
    getPersonalDataStatus: builder.query<ISubscriberIdStatus, string>({
      query: (subscriberId) =>
        formatSubscriberIdApiUrl(subscriberId, 'personal-data/status'),
      providesTags: ['PersonalDataMeta'],
    }),
    getPersonalDataInfo: builder.query<
      IPersonalDataInfo,
      IPersonalDataInfoQuery
    >({
      query: ({ subscriberId }) =>
        formatSubscriberIdApiUrl(subscriberId, 'personal-data/info'),
      transformResponse: (response: IPersonalDataInfoResponse, _, arg) =>
        personalDataInfoMapping(response, arg.defaultDoc),
      providesTags: ['PersonalDataMeta', 'PersonalDataInfo'],
    }),
    getPersonalDataFile: builder.query<IPersonalDataFile[], string>({
      query: (subscriberId) =>
        formatSubscriberIdApiUrl(subscriberId, 'personal-data/documents'),
      transformResponse: (response: IPersonalDataFileResponse[]) =>
        personalDataFileMapping(response),
      providesTags: ['PersonalDataMeta'],
    }),

    updatePersonalDataEmail: builder.mutation<
      void,
      { subscriberId: string; email: string }
    >({
      query: ({ subscriberId, email }) => ({
        url: formatSubscriberIdApiUrl(subscriberId, 'personal-data'),
        method: 'POST',
        body: {
          personalData: { email, emailOnly: true },
        },
      }),
      invalidatesTags: ['PersonalDataInfo'],
    }),

    confirmPersonalData: builder.mutation({
      query: (subscriberId: string) => ({
        url: formatSubscriberIdApiUrl(subscriberId, 'personal-data/confirm'),
        method: 'POST',
      }),
      invalidatesTags: ['PersonalDataMeta'],
    }),

    savePersonalData: builder.mutation<
      IPersonalDataSave,
      IPersonalDataInfoEditQuery
    >({
      query: (arg) => ({
        url: formatSubscriberIdApiUrl(arg.subscriberId, 'personal-data'),
        method: 'POST',
        body: personalDataSaveEditRequestMapping(
          arg.personalData,
          arg.excludeFiles,
          arg.withoutConfirm,
        ),
      }),
    }),

    uploadPersonalDataFile: builder.mutation<
      { fileId: number },
      IPersonalDataFileUploadQuery
    >({
      query: ({ file, subscriberId }) => {
        const data = new FormData();
        data.append('file', file);
        return {
          url: formatSubscriberIdApiUrl(
            subscriberId,
            'personal-data/document/upload',
          ),
          method: 'POST',
          headers: {
            'accept': 'application/json;charset=UTF-8',
            'X-NODE': 'sticky',
          },
          body: data,
        };
      },
      transformResponse: (response: { fileId: number }, _, arg) => ({
        file: arg.file,
        fileId: FileId(response.fileId),
      }),
    }),

    searchPersonalDataNationality: builder.query<IInputSearchItem[], string>({
      query: (value: string) => ({
        url: '/address/air/search/elements?type=country',
        method: 'POST',
        body: {
          country: {
            def: value,
          },
        },
      }),
      transformResponse: (response: IFormAddressSearchResponse) =>
        formAddressSearchMapping(response),
    }),

    removePersonalDataNewFile: builder.mutation<void, TNewFileId>({
      query: (fileId: TNewFileId) => ({
        url: `/subscriber/personal-data/document/${fileId}`,
        method: 'DELETE',
      }),
    }),
    removePersonalDataAllFiles: builder.mutation<any, IPersonalDataNewFile[]>({
      async queryFn(files, _queryApi, _extraOptions, baseQuery) {
        const results = await Promise.all(
          files.map((file) =>
            baseQuery(`/subscriber/personal-data/document/${file.fileId}`),
          ),
        );

        const merged = [].concat(...results.map((result) => result.data));
        const errors = [].concat(
          ...results
            .filter((result) => result.error != null)
            .map((result) => result.error),
        );

        if (errors.length > 0) return { error: errors };

        return { data: merged };
      },
    }),

    deletePersonalData: builder.mutation<{ conflicts?: TConflicts }, string>({
      query: (subscriberId) => ({
        url: formatSubscriberIdApiUrl(subscriberId, 'personal-data/delete'),
        method: 'POST',
      }),
      invalidatesTags: ['PersonalDataMeta'],
    }),
  }),
});

export const {
  useGetPersonalDataMetaQuery,
  useGetPersonalDataStatusQuery,
  useGetPersonalDataInfoQuery,
  useGetPersonalDataFileQuery,

  useUpdatePersonalDataEmailMutation,

  useConfirmPersonalDataMutation,

  useSavePersonalDataMutation,

  useUploadPersonalDataFileMutation,

  useSearchPersonalDataNationalityQuery,

  useRemovePersonalDataNewFileMutation,
  useRemovePersonalDataAllFilesMutation,

  useDeletePersonalDataMutation,
} = personalDataApi;
